import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { FaArrowLeft, FaBold, FaCheckCircle, FaChevronDown, FaClock, FaEye, FaImage, FaInfoCircle, FaItalic, FaMobileAlt, FaPlus, FaSyncAlt, FaTimesCircle, FaTrash, FaUnderline, FaVideo, FaWindowClose } from 'react-icons/fa';
import { BUILD_AT } from '../buildInfo';
import { Sidebar } from '../components/Note';
import api from '../api';
import '../styles/tailwind.output.css';
import '../styles/quill-overrides.css';
import '../styles/plantillas-light-overrides.css';

function PlantillasCoexistencia() {
  const [dark, setDark] = useState(() => {
    if (typeof document === 'undefined') return true;
    return document.documentElement.classList.contains('dark');
  });
  const [buildStamp, setBuildStamp] = useState(BUILD_AT);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activePhoneId, setActivePhoneId] = useState('');
  const [activeDisplay, setActiveDisplay] = useState('');
  const [activeWabaId, setActiveWabaId] = useState('');
  const [onlyActive, setOnlyActive] = useState(false);
  const [sortMode, setSortMode] = useState('recent');
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [myNumbers, setMyNumbers] = useState([]);
  // Estado para detectar si el editor se inicializa; si falla, activamos textarea fallback
  const [editorReady, setEditorReady] = useState(false);
  const [editorFallback, setEditorFallback] = useState(false);
  const editorReadyRef = useRef(false);
  // Usar TinyMCE auto-hospedado bajo /assets con cache-buster por sesión
  // para evitar respuestas antiguas cacheadas (p.ej. HTML del fallback SPA).
  const [tinymceSrc, setTinymceSrc] = useState(
    () => `/assets/tinymce/tinymce.min.js?v=${encodeURIComponent(`${BUILD_AT || 'v'}-${Date.now()}`)}`
  );
  const [editorMountKey, setEditorMountKey] = useState(0);
  const appVersion = (() => {
    const withEnv = import.meta.env.VITE_APP_VERSION;
    if (withEnv) return withEnv;
    try {
      const fmt = new Intl.DateTimeFormat('es-EC', {
        timeZone: 'America/Guayaquil',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false
      });
      const parts = Object.fromEntries(fmt.formatToParts(new Date()).map(p => [p.type, p.value]));
      // Formato: YYYY-MM-DD HH:MM
      return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
    } catch (_) {
      return new Date().toISOString().slice(0, 16).replace('T', ' ');
    }
  })();
  const [formData, setFormData] = useState({
    name: '',
    category: 'MARKETING',
    language: 'es',
    header_type: '',
    header_text: '',
    body_text: '',
    footer_text: '',
    buttons: []
  });
  const [headerMediaUrl, setHeaderMediaUrl] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const bodyTextareaRef = useRef(null);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const sync = () => setDark(root.classList.contains('dark'));
    sync();

    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });

    const onStorage = (event) => {
      if (event.key === 'theme') sync();
    };
    window.addEventListener('storage', onStorage);

    return () => {
      observer.disconnect();
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Banner runtime: leer build.txt con cache-busting para reflejar último build
  useEffect(() => {
    const url = `/build.txt?ts=${Date.now()}`;
    fetch(url, { cache: 'no-store' })
      .then(r => r.ok ? r.text() : Promise.reject(new Error('No build.txt')))
      .then(txt => { if (txt && txt.trim()) setBuildStamp(txt.trim()); })
      .catch(() => { /* fallback a BUILD_AT */ });
  }, []);
  
  const editorRef = useRef(null);
  const handleInsertMedia = useCallback((type) => {
    try {
      const ed = editorRef.current;
      if (!ed) return;
      const token = type === 'image' ? '[IMG]' : '[MEDIA]';
      ed.insertContent(token + ' ');
    } catch (_) {}
  }, []);

  const insertBodyVariable = useCallback((kind) => {
    const token = kind === 'nombre' ? '{{1}}' : `{{${kind}}}`;
    const ed = editorRef.current;
    if (ed && !editorFallback) {
      try {
        ed.focus();
        ed.insertContent(`${token} `);
        return;
      } catch (_) {}
    }

    const ta = bodyTextareaRef.current;
    if (ta && typeof ta.selectionStart === 'number') {
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const src = String(formData.body_text || '');
      const next = src.slice(0, start) + token + ' ' + src.slice(end);
      setFormData(prev => ({ ...prev, body_text: next }));
      requestAnimationFrame(() => {
        try {
          ta.focus();
          const pos = start + token.length + 1;
          ta.selectionStart = pos;
          ta.selectionEnd = pos;
        } catch (_) {}
      });
      return;
    }

    setFormData(prev => ({ ...prev, body_text: `${String(prev.body_text || '')}${token} ` }));
  }, [editorFallback, formData.body_text]);

  const applyInlineFormat = useCallback((kind) => {
    const marker = kind === 'bold' ? '*' : (kind === 'italic' ? '_' : '~');
    const ed = editorRef.current;
    if (ed && !editorFallback) {
      try {
        ed.focus();
        if (kind === 'bold') ed.execCommand('Bold');
        if (kind === 'italic') ed.execCommand('Italic');
        if (kind === 'strike') ed.execCommand('Strikethrough');
        return;
      } catch (_) {}
    }

    const ta = bodyTextareaRef.current;
    if (ta && typeof ta.selectionStart === 'number') {
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const src = String(formData.body_text || '');
      const selected = src.slice(start, end) || 'texto';
      const wrapped = `${marker}${selected}${marker}`;
      const next = src.slice(0, start) + wrapped + src.slice(end);
      setFormData(prev => ({ ...prev, body_text: next }));
      requestAnimationFrame(() => {
        try {
          ta.focus();
          const pos = start + wrapped.length;
          ta.selectionStart = pos;
          ta.selectionEnd = pos;
        } catch (_) {}
      });
      return;
    }

    setFormData(prev => ({ ...prev, body_text: `${String(prev.body_text || '')}${marker}texto${marker}` }));
  }, [editorFallback, formData.body_text]);

  const toAbsolute = (u) => {
    try {
      const s = String(u || '').trim();
      if (!s) return '';
      if (s.startsWith('http://') || s.startsWith('https://')) return s;
      if (s.startsWith('//')) return 'https:' + s;
      let path = s;
      if (s.startsWith('api/')) path = '/' + s;
      else if (s.startsWith('meta-templates/header-media/')) path = '/api/' + s;
      else if (!s.startsWith('/')) path = '/' + s;
      const origin = (window.location?.origin || '').replace('http://', 'https://');
      return origin + path;
    } catch {
      return String(u || '');
    }
  };

  const htmlToPlain = (html) => {
    try {
      let s = String(html || '');
      s = s.replace(/<br\s*\/?>/gi, '\n');
      s = s.replace(/<\/p>/gi, '\n');
      s = s.replace(/<p[^>]*>/gi, '');
      s = s.replace(/<[^>]+>/g, '');
      const el = document.createElement('textarea');
      el.innerHTML = s;
      s = el.value;
      s = s.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      s = s.replace(/[ \t\f\v]+/g, ' ');
      s = s.replace(/ *\n */g, '\n');
      s = s.replace(/\n{3,}/g, '\n\n');
      return s.trim();
    } catch {
      return String(html || '');
    }
  };

  const canSubmitToMetaStatus = (statusRaw) => {
    const s = String(statusRaw || '').trim().toUpperCase();
    return ['DRAFT', 'FAILED', 'REJECTED', 'BORRADOR'].includes(s);
  };

  const buildTemplateForSubmit = (src) => {
    const baseId = src?.primary?.id || src?.id || null;
    const basePid = src?.primary?.phone_number_id || src?.phone_number_id || src?.phoneNumberId || '';
    const baseStatus = src?.primary?.status || src?.status || 'DRAFT';
    const baseBody = src?.primary?.body_text_html || src?.body_text_html || src?.primary?.body_text || src?.body_text || '';
    const baseHeaderType = src?.primary?.header_type || src?.header_type || src?.headerType || '';
    const baseHeaderMedia = src?.primary?.header_media_url || src?.header_media_url || '';
    return {
      id: baseId,
      name: String(src?.name || src?.display_name || 'plantilla'),
      phone_number_id: String(basePid || ''),
      status: String(baseStatus || '').toUpperCase(),
      body_text: String(baseBody || ''),
      header_type: String(baseHeaderType || '').toUpperCase(),
      header_media_url: String(baseHeaderMedia || ''),
    };
  };

  const openPreview = (g) => {
    try {
      const bodyHtml = String(g?.body_text_html || g?.body_text || '');
      const plain = htmlToPlain(bodyHtml);
      const submitTemplate = buildTemplateForSubmit(g);
      const mVid = bodyHtml.match(/<video[^>]+src="([^"]+)"/i);
      const mImg = bodyHtml.match(/<img[^>]+src="([^"]+)"/i);
      let mediaUrl = String(g?.header_media_url || '').trim();
      let mediaType = String(g?.header_type || '').toUpperCase();

      if (!mediaUrl && mVid && mVid[1]) {
        mediaUrl = mVid[1];
        mediaType = 'VIDEO';
      } else if (!mediaUrl && mImg && mImg[1]) {
        mediaUrl = mImg[1];
        mediaType = 'IMAGE';
      }

      if (!mediaType && mediaUrl) {
        const lower = mediaUrl.toLowerCase();
        mediaType = lower.match(/\.pdf|\.docx?|\.xlsx?$/) ? 'DOCUMENT' : (lower.match(/\.mp4|\.mov|\.webm|\.m4v$/) ? 'VIDEO' : 'IMAGE');
      }

      setPreviewTemplate({
        name: g?.display_name || g?.name || 'Plantilla',
        language: g?.language || 'es',
        status: submitTemplate.status,
        metaTemplateId: g?.meta_template_id || '',
        text: plain,
        mediaUrl: toAbsolute(mediaUrl),
        mediaType,
        submitTemplate,
        editorTemplate: {
          id: submitTemplate.id || g?.primary?.id || g?.id || null,
          name: String(g?.name || g?.display_name || ''),
          category: String(g?.category || 'MARKETING'),
          language: String(g?.language || 'es'),
          header_type: String(g?.header_type || '').toUpperCase(),
          header_text: '',
          body_text: String(g?.body_text_html || g?.body_text || ''),
          footer_text: '',
          buttons: [],
          status: String(submitTemplate.status || ''),
          header_media_url: String(g?.header_media_url || ''),
        },
      });
      setPreviewOpen(true);
    } catch (_) {
      setPreviewTemplate(null);
      setPreviewOpen(false);
    }
  };

  // Mantener referencias estables de módulos y formatos para evitar reinit al teclear
  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'font': [] }],
        [{ 'size': [] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        [{ 'header': 1 }, { 'header': 2 }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['blockquote', 'code'],
        ['image'],
        ['clean']
      ],
      handlers: {
        image: () => {
          try {
            const editor = quillRef.current?.getEditor?.();
            if (!editor) return;
            const range = editor.getSelection(true) || { index: 0 };
            const token = '[IMG]';
            editor.insertText(range.index, token + ' ');
            editor.setSelection(range.index + token.length + 1);
          } catch (_) {}
        }
      }
    },
    keyboard: { bindings: {} }
  }), []);

  const quillFormats = useMemo(() => ([
    'font','size','bold','italic','underline','strike','color','background',
    'script','header','list','bullet','indent','align','blockquote','code',
    'image'
  ]), []);
  
  useEffect(() => {
    loadTemplates();
  }, []);

  // Cuando el formulario se muestra, arrancamos un temporizador para caer a textarea si TinyMCE no se inicializa.
  useEffect(() => {
    if (!showForm) {
      setEditorReady(false);
      editorReadyRef.current = false;
      setEditorFallback(false);
      return;
    }
    let cancelled = false;

    const prepareEditor = async () => {
      // Al abrir el formulario, limpiar estados y validar fuente de TinyMCE.
      setEditorFallback(false);
      editorReadyRef.current = false;
      setEditorReady(false);

      const localBase = '/assets/tinymce/tinymce.min.js';
      const localWithBust = `${localBase}?v=${encodeURIComponent(`${BUILD_AT || 'v'}-${Date.now()}`)}`;
      const cdnWithBust = `https://cdn.jsdelivr.net/npm/tinymce@8/tinymce.min.js?v=${encodeURIComponent(`${BUILD_AT || 'v'}-${Date.now()}`)}`;

      let nextSrc = localWithBust;
      try {
        const resp = await fetch(localBase, { method: 'HEAD', cache: 'no-store' });
        if (!resp.ok) {
          nextSrc = cdnWithBust;
        }
      } catch {
        nextSrc = cdnWithBust;
      }

      if (cancelled) return;
      setTinymceSrc(nextSrc);
      setEditorMountKey((k) => k + 1);

      const timeout = setTimeout(() => {
        if (!editorReadyRef.current) {
          setEditorFallback(true);
        }
      }, 15000);

      if (!cancelled) {
        return () => clearTimeout(timeout);
      }
      clearTimeout(timeout);
      return undefined;
    };

    let cleanup;
    prepareEditor().then((maybeCleanup) => {
      cleanup = maybeCleanup;
    });

    return () => {
      cancelled = true;
      if (typeof cleanup === 'function') cleanup();
    };
  }, [showForm]);

  // Verificar disponibilidad del script local de TinyMCE y aplicar fallback al CDN si no se sirve correctamente
  // Nota: al servir bajo /assets, Nginx ya entrega archivos estáticos sin caer al SPA
  
  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      // Resolver número activo de forma robusta: primero connection-status (fuente de verdad), luego meta-my-connection/meta-active-number
      try {
        const st = await api.get('integration/connection-status/');
        const d = st.data?.details || {};
        if (st.data?.is_connected && d.phone_number_id) {
          setActivePhoneId(String(d.phone_number_id));
          setActiveDisplay(String(d.display_phone_number || ''));
          setActiveWabaId(String(d.waba_id || ''));
        }
      } catch (_) {}

      try {
        const myc = await api.get('integration/meta-my-connection/');
        const nums = Array.isArray(myc.data?.numbers) ? myc.data.numbers : [];
        setMyNumbers(nums);
        if (!activePhoneId && nums.length) {
          const first = nums[0];
          setActivePhoneId(String(first.phone_number_id || first.phoneNumberId || first.id || ''));
        }
      } catch (_) {
        // Ignorar si endpoint no existe
      }

      // Cargar plantillas del usuario
      try {
        const resp = await api.get('meta-templates/?all=1');
        const data = resp.data || [];
        setTemplates(Array.isArray(data) ? data : (data.results || []));
      } catch (_) {
        setTemplates([]);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  // Meta solo acepta placeholders numericos {{1}}, {{2}}, ...
  // Soportamos alias de UX {{nombre}} y lo normalizamos a {{1}}.
  const normalizeNamedVariablesInBody = (html) => {
    try {
      let s = String(html || '');
      s = s.replace(/\{\{\s*nombre\s*\}\}/gi, '{{1}}');
      s = s.replace(/\{\{\s*(\d+)\s*\}\}/g, '{{$1}}');
      return s;
    } catch {
      return String(html || '');
    }
  };

  const validateMetaVariableFormat = (html) => {
    const src = normalizeNamedVariablesInBody(String(html || ''));
    const text = src.replace(/<[^>]+>/g, ' ');
    const all = Array.from(text.matchAll(/\{\{([^{}]+)\}\}/g));
    const invalid = all
      .map(m => String(m[1] || '').trim())
      .filter(v => !/^\d+$/.test(v));
    if (invalid.length) {
      return {
        ok: false,
        message: 'Meta solo permite variables numericas: {{1}}, {{2}}, etc. Revisa estos valores: ' + invalid.join(', '),
      };
    }

    const nums = Array.from(new Set(all.map(m => parseInt(String(m[1] || '').trim(), 10)).filter(n => Number.isFinite(n) && n > 0))).sort((a, b) => a - b);
    if (nums.length) {
      const expected = Array.from({ length: nums[nums.length - 1] }, (_, i) => i + 1);
      const sequential = nums.length === expected.length && nums.every((n, i) => n === expected[i]);
      if (!sequential) {
        return {
          ok: false,
          message: 'Meta requiere variables consecutivas desde {{1}} sin saltos. Ejemplo valido: {{1}}, {{2}}, {{3}}.',
        };
      }
    }

    return { ok: true, body: src };
  };

  const persistDraftTemplate = async ({ showSuccessMessage = true } = {}) => {
    const payload = { ...formData };
    payload.body_text = normalizeNamedVariablesInBody(payload.body_text);
    payload.name = String(payload.name || '').trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
    if (activePhoneId) {
      payload.phone_number_id = String(activePhoneId);
    }
    if (headerMediaUrl) {
      payload.header_media_url = headerMediaUrl;
    }

    if (editingTemplate?.id) {
      await api.put(`meta-templates/${editingTemplate.id}/`, payload);
      if (showSuccessMessage) {
        alert('Plantilla actualizada');
      }
      return buildTemplateForSubmit({
        id: editingTemplate.id,
        name: payload.name,
        status: editingTemplate.status || 'DRAFT',
        phone_number_id: payload.phone_number_id || editingTemplate.phone_number_id || '',
        body_text_html: payload.body_text,
        header_type: payload.header_type,
        header_media_url: payload.header_media_url || '',
      });
    }

    const resp = await api.post('meta-templates/', payload);
    const created = (resp?.data && typeof resp.data === 'object') ? resp.data : {};
    let createdId = created?.id || null;
    let createdPid = String(created?.phone_number_id || payload.phone_number_id || '');
    let createdStatus = String(created?.status || 'DRAFT');

    if (!createdId) {
      try {
        const listResp = await api.get('meta-templates/?all=1');
        const arr = Array.isArray(listResp.data) ? listResp.data : (listResp.data?.results || []);
        const matches = (arr || [])
          .filter((t) => String(t?.name || '') === String(payload.name || ''))
          .filter((t) => !createdPid || String(t?.phone_number_id || '') === createdPid)
          .sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
        if (matches.length) {
          createdId = matches[0]?.id || createdId;
          createdPid = String(matches[0]?.phone_number_id || createdPid || '');
          createdStatus = String(matches[0]?.status || createdStatus || 'DRAFT');
        }
      } catch (_) {
        // Si falla la búsqueda, el submit posterior mostrará mensaje claro.
      }
    }

    if (showSuccessMessage) {
      alert('Plantilla creada en borrador');
    }

    return buildTemplateForSubmit({
      id: createdId,
      name: payload.name,
      status: createdStatus,
      phone_number_id: createdPid,
      body_text_html: payload.body_text,
      header_type: payload.header_type,
      header_media_url: payload.header_media_url || '',
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSavingTemplate(true);
    try {
      await persistDraftTemplate({ showSuccessMessage: true });
      setShowForm(false);
      setEditingTemplate(null);
      resetForm();
      loadTemplates();
    } catch (e) {
      const errors = e.response?.data;
      if (errors && typeof errors === 'object') {
        const errorMsg = Object.entries(errors).map(([k, v]) => `${k}: ${v}`).join('\n');
        alert('Error al guardar:\n' + errorMsg);
      } else {
        alert('Error al guardar: ' + (e.response?.data?.detail || e.message));
      }
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleSaveAndSubmit = async () => {
    if (savingTemplate) return;
    if (!isNameValid || !isBodyValid) return;
    setSavingTemplate(true);
    try {
      const savedTemplate = await persistDraftTemplate({ showSuccessMessage: false });
      if (!savedTemplate?.id) {
        alert('No se pudo identificar el borrador recién guardado para enviarlo a Meta. Intenta sincronizar y volver a enviar.');
        return;
      }
      if (!editingTemplate && savedTemplate?.id) {
        setEditingTemplate({ id: savedTemplate.id, status: savedTemplate.status || 'DRAFT' });
      }

      const sent = await handleSubmitToMeta(savedTemplate);
      if (sent) {
        setShowForm(false);
        setEditingTemplate(null);
        resetForm();
      }
      await loadTemplates();
    } catch (e) {
      const errors = e.response?.data;
      if (errors && typeof errors === 'object') {
        const errorMsg = Object.entries(errors).map(([k, v]) => `${k}: ${v}`).join('\n');
        alert('Error al preparar envío:\n' + errorMsg);
      } else {
        alert('Error al enviar a Meta: ' + (e.response?.data?.detail || e.message));
      }
    } finally {
      setSavingTemplate(false);
    }
  };
  
  const uploadHeaderMediaInteractively = async (expectedType) => {
    return new Promise((resolve) => {
      try {
        const input = document.createElement('input');
        input.type = 'file';
        if (expectedType === 'IMAGE') input.accept = 'image/*';
        else if (expectedType === 'VIDEO') input.accept = 'video/*';
        else input.accept = '*/*';
        input.onchange = async () => {
          try {
            const file = input.files && input.files[0];
            if (!file) return resolve({ url: '', type: '' });
            const fd = new FormData();
            fd.append('file', file);
            const resp = await api.post('meta-templates/upload-header-media/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            const url = resp.data?.url || '';
            const type = (resp.data?.type || '').toUpperCase();
            resolve({ url, type });
          } catch (e) {
            alert('Fallo al subir archivo: ' + (e.response?.data?.detail || e.message));
            resolve({ url: '', type: '' });
          }
        };
        input.click();
      } catch (_) {
        resolve({ url: '', type: '' });
      }
    });
  };

  const handleSubmitToMeta = async (template) => {
    if (!template?.id) {
      alert('No se encontró el ID de la plantilla para enviarla a Meta.');
      return false;
    }
    const tplPid = String(template.phone_number_id || template.phoneNumberId || '');
    if (!window.confirm(
      `¿Enviar plantilla "${template.name}" a Meta para revisión?` +
      `\n\nNota: se usará el WABA asociado al número de la plantilla (${tplPid || '—'}).` +
      `\nUna vez enviada, no podrás editarla hasta que sea aprobada o rechazada.`
    )) return false;

    const bodyCheck = validateMetaVariableFormat(String(template.body_text || template.bodyText || ''));
    if (!bodyCheck.ok) {
      alert(bodyCheck.message);
      return false;
    }
    
    // Intentar derivar URL de media del body (si viene)
    let mediaUrl = '';
    let mediaType = '';
    const tplHeaderType = String(template.header_type || template.headerType || '').toUpperCase();
    try {
      const body = String(template.body_text || template.bodyText || '');
      const mImg = body.match(/<img[^>]+src=\"([^\"]+)\"/i);
      const mVid = body.match(/<video[^>]+src=\"([^\"]+)\"/i);
      if (mVid && mVid[1]) {
        mediaUrl = mVid[1];
        mediaType = 'VIDEO';
      } else if (mImg && mImg[1]) {
        mediaUrl = mImg[1];
        mediaType = 'IMAGE';
      } else if (headerMediaUrl) {
        mediaUrl = headerMediaUrl;
        mediaType = (formData.header_type || '').toUpperCase();
      }
    } catch (_) {}

    // Si el header es de media pero no detectamos URL, pedir archivo y subir
    if (!mediaUrl && ['IMAGE','VIDEO','DOCUMENT'].includes(tplHeaderType)) {
      const picked = await uploadHeaderMediaInteractively(tplHeaderType);
      if (picked.url) {
        mediaUrl = picked.url;
        mediaType = tplHeaderType;
      }
    }

    try {
      const payload = {};
      if (mediaUrl && mediaType) {
        payload.header_media_url = mediaUrl;
        payload.header_media_type = mediaType;
      }
      const r = await api.post(`meta-templates/${template.id}/submit/`, payload);
      const ctx = r?.data?.context || {};
      const used = r?.data?.used_media ? `\nMedia usada: ${r.data.used_media}` : '';
      alert(`Plantilla enviada a Meta. El proceso de revisión puede tardar 1-2 días.` + (ctx.pid ? `\nPID usado: ${ctx.pid}` : '') + (ctx.waba_id ? `\nWABA: ${ctx.waba_id}` : '') + used);
      loadTemplates();
      return true;
    } catch (e) {
      const d = e.response?.data || {};
      const ctx = d.context || {};
      const metaErr = d.meta_error || {};
      const base = d.detail || d.error || e.message;
      const extra = [];
      if (ctx.pid) extra.push(`PID: ${ctx.pid}`);
      if (ctx.waba_id) extra.push(`WABA: ${ctx.waba_id}`);
      if (ctx.header_media_url) extra.push(`Media: ${ctx.header_media_url}${ctx.header_media_type ? ` (${ctx.header_media_type})` : ''}`);
      const usedHandleId = ctx.used_handle_id || ctx.used_handle;
      if (usedHandleId) extra.push(`Handle: ${usedHandleId}`);
      const fbtrace = metaErr.fbtrace_id || metaErr.fbtraceId;
      if (fbtrace) extra.push(`fbtrace_id: ${fbtrace}`);
      alert(`Error al enviar plantilla: ${base}` + (extra.length ? `\n${extra.join('\n')}` : ''));
      return false;
    }
  };
  
  const handleSyncStatus = async () => {
    setSyncing(true);
    try {
      const payload = activePhoneId ? { phone_number_id: String(activePhoneId) } : {};
      try {
        // Enviar access_token si está almacenado en localStorage (opcional)
        const metaToken = localStorage.getItem('META_ACCESS_TOKEN') || localStorage.getItem('meta_access_token');
        if (metaToken) payload.access_token = metaToken;
      } catch (_) {}
      await api.post('meta-templates/sync/', payload);
      alert('Estados sincronizados con Meta');
      loadTemplates();
    } catch (e) {
      alert('Error al sincronizar: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSyncing(false);
    }
  };

  const handleReconcileActive = async () => {
    if (!activePhoneId) {
      alert('No hay número activo resuelto.');
      return;
    }
    setReconciling(true);
    try {
      await api.post('meta-templates/reconcile/', { phone_number_id: String(activePhoneId) });
      alert('Reconciliación aplicada al número activo');
      await loadTemplates();
    } catch (e) {
      alert('Error al reconciliar: ' + (e.response?.data?.error || e.response?.data?.detail || e.message));
    } finally {
      setReconciling(false);
    }
  };
  
  const handleEdit = (template) => {
    const st = String(template?.status || '').trim().toUpperCase();
    if (!canSubmitToMetaStatus(st)) {
      alert('Solo puedes editar plantillas en estado borrador/rechazada/fallida');
      return;
    }
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      category: template.category,
      language: template.language,
      header_type: template.header_type || '',
      header_text: template.header_text || '',
      body_text: template.body_text,
      footer_text: template.footer_text || '',
      buttons: template.buttons || []
    });
    try {
      setHeaderMediaUrl(String(template.header_media_url || ''));
    } catch (_) {}
    setShowForm(true);
  };
  
  const handleDelete = async (template) => {
    if (!window.confirm(`¿Eliminar plantilla "${template.name}"?`)) return;
    
    try {
      await api.delete(`meta-templates/${template.id}/`);
      alert('Plantilla eliminada');
      loadTemplates();
    } catch (e) {
      alert('Error al eliminar: ' + (e.response?.data?.detail || e.message));
    }
  };
  
  const resetForm = () => {
    setFormData({
      name: '',
      category: 'MARKETING',
      language: 'es',
      header_type: '',
      header_text: '',
      body_text: '',
      footer_text: '',
      buttons: []
    });
  };
  
  const addButton = () => {
    setFormData({
      ...formData,
      buttons: [...formData.buttons, { type: 'QUICK_REPLY', text: '' }]
    });
  };
  
  const removeButton = (index) => {
    setFormData({
      ...formData,
      buttons: formData.buttons.filter((_, i) => i !== index)
    });
  };
  
  const updateButton = (index, field, value) => {
    const newButtons = [...formData.buttons];
    newButtons[index][field] = value;
    setFormData({ ...formData, buttons: newButtons });
  };
  
  const getStatusBadge = (statusRaw) => {
    const status = (statusRaw || '').toUpperCase();
    const badges = {
      DRAFT: {
        label: 'Borrador',
        icon: <FaWindowClose className="text-[10px]" />,
        bg: 'rgba(148,163,184,0.16)',
        color: '#cbd5e1',
      },
      PENDING: {
        label: 'Pendiente',
        icon: <FaClock className="text-[10px]" />,
        bg: 'rgba(210,153,34,0.16)',
        color: '#d29922',
      },
      APPROVED: {
        label: 'Aprobada',
        icon: <FaCheckCircle className="text-[10px]" />,
        bg: 'rgba(37,211,102,0.15)',
        color: '#25d366',
      },
      REJECTED: {
        label: 'Rechazada',
        icon: <FaTimesCircle className="text-[10px]" />,
        bg: 'rgba(248,81,73,0.16)',
        color: '#f85149',
      },
    };
    const badge = badges[status] || badges.DRAFT;
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
        style={{ background: badge.bg, color: badge.color }}
      >
        {badge.icon}
        {badge.label}
      </span>
    );
  };
  
  // Agrupa por nombre de plantilla y prioriza el número activo para acciones
  const groupTemplates = (arr, activePid, onlyActiveFlag, myNums, currentSortMode = 'recent') => {
    // Filtrar por los números conectados del usuario actual
    const allowed = new Set(
      (Array.isArray(myNums) ? myNums : []).map(n => String(n.phone_number_id || n.id || '').trim()).filter(Boolean)
    );
    const byName = new Map();
    const aPid = String(activePid || '').trim();
    (arr || []).forEach(t => {
      const name = String(t.name || '').trim();
      if (!name) return;
      const pid = String(t.phone_number_id || t.phoneNumberId || '').trim();
      // Excluir plantillas que no pertenecen a los números del usuario
      if (allowed.size && !allowed.has(pid)) return;
      const disp = String(t.display_phone_number || t.displayPhoneNumber || '').trim();
      const headerType = String(t.header_type || t.headerType || '').toUpperCase();
      const item = {
        id: t.id,
        phone_number_id: pid,
        display_phone_number: disp,
        status: t.status,
        updated_at: t.updated_at,
        created_at: t.created_at,
        meta_template_id: t.meta_template_id,
        rejection_reason: t.rejection_reason,
        body_text: t.body_text,
        body_text_html: t.body_text,
        body_text_plain: t.body_text_plain,
        header_type: headerType,
        header_media_url: t.header_media_url,
      };
      if (!byName.has(name)) {
        byName.set(name, {
          name,
          category: t.category,
          category_display: t.category_display,
          language: t.language,
          phones: [item],
        });
      } else {
        byName.get(name).phones.push(item);
      }
    });
    let groups = Array.from(byName.values());
    // Mostrar todas las plantillas por usuario: no filtrar por número activo
    // Si se quisiera, "onlyActiveFlag" podría activar este filtro, pero por pedido del usuario lo desactivamos por defecto
    if (false && onlyActiveFlag && aPid) {
      groups = groups.filter(g => g.phones.some(p => String(p.phone_number_id||'') === aPid));
    }
    // Calcula primario (activo si existe) y valores derivados por fila
    groups.forEach(g => {
      const primary = g.phones.find(p => String(p.phone_number_id||'') === aPid) || g.phones[0];
      g.primary = primary;
      g.status = primary?.status || g.phones[0]?.status;
      g.meta_template_id = primary?.meta_template_id || '';
      g.body_text = (primary?.body_text_plain || primary?.body_text || '').toString();
      g.body_text_html = (primary?.body_text_html || primary?.body_text || '').toString();
      g.rejection_reason = primary?.rejection_reason || '';
      g.header_type = String(primary?.header_type || '').toUpperCase();
      g.header_media_url = String(primary?.header_media_url || '');
      const hasHeaderMedia = g.phones.some(p => ['IMAGE','VIDEO','DOCUMENT'].includes(String(p.header_type||'').toUpperCase()));
      const bodyHasMedia = /<img\b|<video\b/i.test(String(g.body_text||''));
      g.has_media = hasHeaderMedia || bodyHasMedia;
      // Nombre para mostrar: sin prefijo p<digits>_
      g.display_name = String(g.name || '').replace(/^p[0-9]+_/, '');

      const latestTs = g.phones.reduce((acc, p) => {
        const parsed = Date.parse(String(p.updated_at || p.created_at || ''));
        return Number.isNaN(parsed) ? acc : Math.max(acc, parsed);
      }, 0);
      const latestId = g.phones.reduce((acc, p) => {
        const n = Number(p.id);
        return Number.isFinite(n) ? Math.max(acc, n) : acc;
      }, 0);
      g.latest_ts = latestTs;
      g.latest_id = latestId;
    });

    if (currentSortMode === 'nameAsc' || currentSortMode === 'nameDesc') {
      const direction = currentSortMode === 'nameAsc' ? 1 : -1;
      groups.sort((a, b) => {
        const aName = String(a.display_name || a.name || '');
        const bName = String(b.display_name || b.name || '');
        return aName.localeCompare(bName, 'es', { sensitivity: 'base', numeric: true }) * direction;
      });
    } else {
      // Predeterminado: más recientes primero (última creada o con cambio de estado)
      groups.sort((a, b) => {
        const byTs = (Number(b.latest_ts || 0) - Number(a.latest_ts || 0));
        if (byTs !== 0) return byTs;
        return Number(b.latest_id || 0) - Number(a.latest_id || 0);
      });
    }

    return groups;
  };

  const groupedTemplates = useMemo(
    () => groupTemplates(templates, activePhoneId, onlyActive, myNumbers, sortMode),
    [templates, activePhoneId, onlyActive, myNumbers, sortMode]
  );
  const approvedCount = groupedTemplates.filter((g) => String(g.status || '').toUpperCase() === 'APPROVED').length;
  const rejectedCount = groupedTemplates.filter((g) => String(g.status || '').toUpperCase() === 'REJECTED').length;
  const pendingCount = groupedTemplates.filter((g) => String(g.status || '').toUpperCase() === 'PENDING').length;
  const categoryOptions = [
    { value: 'MARKETING', label: 'Marketing', desc: 'Promociones, ofertas, anuncios', color: '#bc8cff' },
    { value: 'UTILITY', label: 'Utilidad', desc: 'Confirmaciones, actualizaciones de pedidos', color: '#58a6ff' },
    { value: 'AUTHENTICATION', label: 'Autenticación', desc: 'Códigos OTP, verificación', color: '#d29922' },
  ];
  const selectedCategory = categoryOptions.find((c) => c.value === formData.category) || categoryOptions[0];
  const isNameValid = /^[a-z0-9_]+$/.test(String(formData.name || '')) && String(formData.name || '').length > 0;
  const plainBodyText = htmlToPlain(formData.body_text || '');
  const bodyCharCount = plainBodyText.length;
  const isBodyValid = plainBodyText.trim().length >= 10;
  const canSaveDraft = isNameValid && isBodyValid && !savingTemplate;
  
  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-100 dark:bg-gray-900">
        <Sidebar />
        <main className="flex-1 p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Cargando plantillas...</p>
          </div>
        </main>
      </div>
    );
  }
  
  return (
    <div className={`plantillas-page ${dark ? 'is-dark' : 'is-light'} flex min-h-screen ${dark ? 'bg-gray-900' : 'bg-[#eef3fb]'}`}>
      <Sidebar />
      <main className={`flex-1 p-6 ${dark ? 'bg-[#060f1f]' : 'bg-[#f7fbff]'}`} style={{ fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}>
        <div className="mx-auto w-full max-w-[1280px] space-y-5">
        <div className={`border-b pb-4 ${dark ? 'border-[#1f3048]' : 'border-[#d3dfef]'}`}>
          <div>
            <h1 className={`text-[39px] font-bold leading-[1.05] ${dark ? 'text-gray-100' : 'text-[#14263f]'}`}>Plantillas Coexistencia</h1>
            <p className={`mt-1.5 text-[14px] ${dark ? 'text-[#8b949e]' : 'text-[#4e6889]'}`}>Administra tus plantillas de WhatsApp Business</p>
          </div>
        </div>

        <div>
          <div className="plantillas-info-chip group relative inline-flex items-center gap-2 rounded-full border border-[#2b4f73] bg-[rgba(88,166,255,0.08)] px-3 py-1.5 text-[12px] font-semibold text-[#8ec8ff]">
            <FaInfoCircle className="text-[12px]" />
            <span>Importante (alcance WABA)</span>
            <span className="rounded-full border border-[#2b4f73] bg-[#10243a] px-2 py-0.5 text-[10px] font-medium text-[#9db0c9]">hover</span>

            <div className="pointer-events-none absolute left-0 top-[calc(100%+10px)] z-20 w-[min(860px,92vw)] translate-y-1 rounded-xl border border-[#2b4f73] bg-[#11243d] p-4 opacity-0 shadow-2xl transition-all duration-200 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:translate-y-0 group-focus-within:opacity-100">
              <p className="text-[13px] leading-relaxed text-[#c9d7ea]">
                En Meta las plantillas se crean a nivel del WABA, no por número. La visibilidad por número en el Business Manager puede variar.
                En esta vista, el número activo define desde qué número se gestionan y envían. Proceso de revisión: 1-2 días hábiles. Usa{' '}
                <span className="rounded bg-[#1e3354] px-1.5 py-0.5 text-[11px] text-sky-200" style={{ fontFamily: 'JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>{'{{1}}'}</span>,{' '}
                <span className="rounded bg-[#1e3354] px-1.5 py-0.5 text-[11px] text-sky-200" style={{ fontFamily: 'JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>{'{{2}}'}</span>{' '}
                o{' '}
                <span className="rounded bg-[#1e3354] px-1.5 py-0.5 text-[11px] text-sky-200" style={{ fontFamily: 'JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>{'{{nombre}}'}</span>{' '}
                (se convierte automáticamente a{' '}
                <span className="rounded bg-[#1e3354] px-1.5 py-0.5 text-[11px] text-sky-200" style={{ fontFamily: 'JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>{'{{1}}'}</span>).
              </p>
            </div>
          </div>
        </div>

        <div className="plantillas-toolbar space-y-2">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[320px] flex-1 max-w-[760px]">
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.08em] text-[#8b949e]">Número activo</label>
              <div className="relative">
                <select
                  className="h-11 w-full appearance-none rounded-lg border border-[#30363d] bg-[#21262d] px-3 pr-9 text-[13px] text-[#e6edf3] outline-none transition focus:border-[#58a6ff]"
                  value={String(activePhoneId || '')}
                  onChange={(e) => {
                    const pid = e.target.value;
                    const found = myNumbers.find((n) => String(n.phone_number_id || n.id || '') === pid);
                    setActivePhoneId(pid);
                    setActiveDisplay(found ? String(found.display_phone_number || '') : activeDisplay);
                    setActiveWabaId('');
                  }}
                  title="Seleccionar número activo manualmente"
                >
                  <option value="">— Seleccionar —</option>
                  {myNumbers.map((n) => (
                    <option key={String(n.phone_number_id || n.id || Math.random())} value={String(n.phone_number_id || n.id || '')}>
                      {String(n.phone_number_id || n.id || '')} / {String(n.display_phone_number || 'Sin número')}
                    </option>
                  ))}
                </select>
                <FaChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-[#8b949e]" />
              </div>
            </div>

            <div className="ml-auto flex flex-wrap items-center gap-2">
            <button
              onClick={handleSyncStatus}
              disabled={syncing}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-[#30363d] bg-transparent px-4 text-[13px] font-semibold text-[#8b949e] transition hover:bg-[#1b2738] hover:text-[#c9d7ea] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaSyncAlt className={`text-[12px] ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Sincronizando...' : 'Sincronizar con Meta'}
            </button>
            <button
              onClick={() => {
                setShowForm(!showForm);
                setEditingTemplate(null);
                resetForm();
              }}
              className={`inline-flex h-10 items-center gap-2 rounded-lg px-4 text-[13px] font-semibold transition ${
                showForm
                  ? 'border border-[#3a4b63] bg-[#202d41] text-[#c9d7ea] hover:bg-[#27364d]'
                  : 'bg-[#25d366] text-[#0d1117] hover:bg-[#2bd76a]'
              }`}
            >
              {showForm ? <FaTimesCircle className="text-[12px]" /> : <FaPlus className="text-[12px]" />}
              {showForm ? 'Cancelar' : 'Nueva Plantilla'}
            </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-[10px] text-[#8b949e]">
            {activePhoneId && <span className="rounded-md border border-[#30363d] bg-[#161b22] px-2 py-1 whitespace-nowrap" style={{ fontFamily: 'JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>{activePhoneId}</span>}
            {activeDisplay && <span className="rounded-md border border-[#30363d] bg-[#161b22] px-2 py-1 whitespace-nowrap" style={{ fontFamily: 'JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>{activeDisplay}</span>}
            {activeWabaId && <span className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-emerald-300 whitespace-nowrap">WABA: {activeWabaId}</span>}
          </div>
        </div>

        {error && (
          <div className="mb-5 rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
            {String(error)}
          </div>
        )}

        {!showForm && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <article className="rounded-xl border p-4 text-center" style={{ background: 'rgba(37,211,102,0.10)', borderColor: 'rgba(37,211,102,0.25)' }}>
              <p className="text-[37px] font-bold leading-none text-[#25d366]">{approvedCount}</p>
              <p className="mt-1 text-[11px] font-medium uppercase tracking-[0.08em] text-[#8b949e]">Aprobadas</p>
            </article>
            <article className="rounded-xl border p-4 text-center" style={{ background: 'rgba(248,81,73,0.10)', borderColor: 'rgba(248,81,73,0.25)' }}>
              <p className="text-[37px] font-bold leading-none text-[#f85149]">{rejectedCount}</p>
              <p className="mt-1 text-[11px] font-medium uppercase tracking-[0.08em] text-[#8b949e]">Rechazadas</p>
            </article>
            <article className="rounded-xl border p-4 text-center" style={{ background: 'rgba(210,153,34,0.10)', borderColor: 'rgba(210,153,34,0.25)' }}>
              <p className="text-[37px] font-bold leading-none text-[#d29922]">{pendingCount}</p>
              <p className="mt-1 text-[11px] font-medium uppercase tracking-[0.08em] text-[#8b949e]">Pendientes</p>
            </article>
          </div>
        )}
        
        {showForm && (
          <section className="plantillas-form-shell overflow-hidden rounded-xl border border-[#30363d] bg-[#0b1423] shadow-2xl">
            <div className="plantillas-form-header flex flex-wrap items-center justify-between gap-3 border-b border-[#30363d] bg-[rgba(255,255,255,0.01)] px-4 py-3">
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingTemplate(null); resetForm(); }}
                  className="inline-flex items-center gap-2 text-sm text-[#8b949e] transition hover:text-[#c9d7ea]"
                >
                  <FaArrowLeft className="text-[12px]" />
                  Volver a plantillas
                </button>
                <div className="h-5 w-px bg-[#30363d]" />

                <div className="flex items-center gap-2 text-xs">
                  <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full font-bold ${isNameValid ? 'bg-[#25d366] text-[#0d1117]' : 'border border-[#25d366] bg-[#1a2a3f] text-[#25d366]'}`}>
                    {isNameValid ? <FaCheckCircle className="text-[11px]" /> : '1'}
                  </span>
                  <span className={`${isNameValid ? 'text-[#e6edf3]' : 'text-[#8b949e]'}`}>Identidad</span>
                  <span className="mx-1 h-px w-8 bg-[#30363d]" />

                  <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full font-bold ${isBodyValid ? 'bg-[#25d366] text-[#0d1117]' : 'border border-[#25d366] bg-[#1a2a3f] text-[#25d366]'}`}>
                    {isBodyValid ? <FaCheckCircle className="text-[11px]" /> : '2'}
                  </span>
                  <span className={`${isBodyValid ? 'text-[#e6edf3]' : 'text-[#8b949e]'}`}>Contenido</span>
                  <span className="mx-1 h-px w-8 bg-[#30363d]" />

                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#25d366] text-[#0d1117]">
                    <FaCheckCircle className="text-[11px]" />
                  </span>
                  <span className="text-[#e6edf3]">Extras</span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingTemplate(null); resetForm(); }}
                  className="h-9 rounded-lg border border-[#30363d] bg-transparent px-4 text-sm text-[#8b949e] transition hover:bg-[#1b2738] hover:text-[#c9d7ea]"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  form="template-builder-form"
                  disabled={!canSaveDraft}
                  className={`h-9 rounded-lg px-4 text-sm font-semibold transition ${canSaveDraft ? 'border border-[#25d36666] bg-[#25d3661f] text-[#25d366] hover:bg-[#25d3662a]' : 'cursor-not-allowed bg-[#21262d] text-[#6b7788]'}`}
                >
                  {savingTemplate ? 'Guardando...' : 'Guardar borrador'}
                </button>
                <button
                  type="button"
                  onClick={handleSaveAndSubmit}
                  disabled={!canSaveDraft}
                  className={`h-9 rounded-lg px-4 text-sm font-semibold transition ${canSaveDraft ? 'border border-[#58a6ff66] bg-[#58a6ff1f] text-[#8ec8ff] hover:bg-[#58a6ff2a]' : 'cursor-not-allowed bg-[#21262d] text-[#6b7788]'}`}
                >
                  {savingTemplate ? 'Enviando...' : 'Enviar a Meta →'}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.25fr)_390px]">
              <form id="template-builder-form" onSubmit={handleSubmit} className="plantillas-form max-h-[calc(100vh-260px)] space-y-6 overflow-y-auto border-r border-[#30363d] p-6 xl:p-7">
                <div className="rounded-lg border border-[#2b4f73] bg-[rgba(88,166,255,0.06)] px-4 py-3 text-xs text-[#9cb0c8]">
                  <p>
                    <span className="font-semibold text-[#58a6ff]">Alcance WABA:</span> Las plantillas se aprueban en 1-2 días hábiles. Usa{' '}
                    <span className="rounded bg-[#1e3354] px-1 py-0.5 text-[11px] text-sky-200" style={{ fontFamily: 'JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>{'{{1}}'}</span>{' '}
                    y{' '}
                    <span className="rounded bg-[#1e3354] px-1 py-0.5 text-[11px] text-sky-200" style={{ fontFamily: 'JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>{'{{nombre}}'}</span>{' '}
                    para variables dinámicas.
                  </p>
                </div>

                <section>
                  <div className="mb-4 flex items-center gap-2">
                    <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full font-bold ${isNameValid ? 'bg-[#25d366] text-[#0d1117]' : 'border border-[#25d366] bg-[#1a2a3f] text-[#25d366]'}`}>
                      {isNameValid ? <FaCheckCircle className="text-[11px]" /> : '1'}
                    </span>
                    <h2 className="text-sm font-semibold text-[#e6edf3]">Identidad de la plantilla</h2>
                  </div>

                  <div className="mb-4">
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.08em] text-[#8b949e]">
                      Nombre interno <span className="text-[#f85149]">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                      className="w-full rounded-lg border bg-[#21262d] px-3 py-2.5 text-sm text-[#e6edf3] outline-none transition"
                      style={{ borderColor: isNameValid ? 'rgba(37,211,102,0.4)' : '#30363d', fontFamily: 'JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}
                      placeholder="ejemplo_plantilla"
                      pattern="[a-z0-9_]+"
                      required
                    />
                    <p className="mt-1.5 text-xs text-[#8b949e]">Solo minúsculas, números y guiones bajos. Ej: promo_navidad_2025</p>
                  </div>

                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.08em] text-[#8b949e]">
                        Categoría <span className="text-[#f85149]">*</span>
                      </label>
                      <div className="space-y-2">
                        {categoryOptions.map((cat) => {
                          const selected = formData.category === cat.value;
                          return (
                            <button
                              key={cat.value}
                              type="button"
                              onClick={() => setFormData({ ...formData, category: cat.value })}
                              className="w-full rounded-lg border px-3 py-2.5 text-left transition"
                              style={{ background: selected ? `${cat.color}16` : '#21262d', borderColor: selected ? `${cat.color}66` : '#30363d' }}
                            >
                              <div className="flex items-start gap-2">
                                <span className="mt-1 h-2 w-2 rounded-full" style={{ background: selected ? cat.color : '#4b5563' }} />
                                <span>
                                  <span className="block text-sm font-semibold text-[#e6edf3]">{cat.label}</span>
                                  <span className="block text-xs text-[#8b949e]">{cat.desc}</span>
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.08em] text-[#8b949e]">
                        Idioma <span className="text-[#f85149]">*</span>
                      </label>
                      <div className="relative mb-3">
                        <select
                          value={formData.language}
                          onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                          className="h-10 w-full appearance-none rounded-lg border border-[#30363d] bg-[#21262d] px-3 pr-9 text-sm text-[#e6edf3] outline-none"
                          required
                        >
                          <option value="es">Español</option>
                          <option value="es_MX">Español (México)</option>
                          <option value="es_AR">Español (Argentina)</option>
                          <option value="en">Inglés</option>
                          <option value="pt_BR">Portugués (Brasil)</option>
                        </select>
                        <FaChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-[#8b949e]" />
                      </div>

                      <div className="rounded-lg border border-[#30363d] bg-[rgba(255,255,255,0.02)] p-3 text-xs text-[#8b949e]">
                        <p className="mb-1 font-semibold text-[#e6edf3]">¿Qué categoría elegir?</p>
                        {categoryOptions.map((cat) => (
                          <p key={`hint-${cat.value}`} className="mb-1 flex items-start gap-1.5 last:mb-0">
                            <span className="mt-1 h-1.5 w-1.5 rounded-full" style={{ background: cat.color }} />
                            <span><strong className="text-[#e6edf3]">{cat.label}:</strong> {cat.desc}</span>
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </section>

                <div className="h-px bg-[#30363d]" />

                <section>
                  <div className="mb-4 flex items-center gap-2">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-[#25d366] bg-[#1a2a3f] text-[#25d366] font-bold">2</span>
                    <h2 className="text-sm font-semibold text-[#e6edf3]">Encabezado <span className="ml-1 text-xs font-normal text-[#8b949e]">(opcional)</span></h2>
                  </div>

                  <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
                    {[
                      { value: '', label: 'Ninguno', icon: '✕' },
                      { value: 'TEXT', label: 'Texto', icon: 'T' },
                      { value: 'IMAGE', label: 'Imagen', icon: <FaImage className="text-[13px]" /> },
                      { value: 'VIDEO', label: 'Video', icon: <FaVideo className="text-[13px]" /> },
                      { value: 'DOCUMENT', label: 'Doc', icon: '↗' },
                    ].map((opt) => {
                      const selected = String(formData.header_type || '') === String(opt.value);
                      return (
                        <button
                          key={`header-${opt.value || 'none'}`}
                          type="button"
                          onClick={() => setFormData({ ...formData, header_type: opt.value })}
                          className="flex flex-col items-center gap-1.5 rounded-lg border py-3 text-xs font-semibold transition"
                          style={{ background: selected ? 'rgba(37,211,102,0.10)' : '#21262d', borderColor: selected ? 'rgba(37,211,102,0.4)' : '#30363d', color: selected ? '#25d366' : '#8b949e' }}
                        >
                          <span>{opt.icon}</span>
                          <span>{opt.label}</span>
                        </button>
                      );
                    })}
                  </div>

                  {formData.header_type === 'TEXT' && (
                    <input
                      type="text"
                      value={formData.header_text}
                      onChange={(e) => setFormData({ ...formData, header_text: e.target.value })}
                      className="mt-3 w-full rounded-lg border border-[#30363d] bg-[#21262d] px-3 py-2.5 text-sm text-[#e6edf3] outline-none"
                      placeholder="Texto del encabezado"
                      maxLength={60}
                    />
                  )}
                  {formData.header_type && formData.header_type !== 'TEXT' && (
                    <p className="mt-2 text-xs text-[#8b949e]">El medio ({formData.header_type}) se subirá al enviar el mensaje.</p>
                  )}
                </section>

                <div className="h-px bg-[#30363d]" />

                <section>
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full font-bold ${isBodyValid ? 'bg-[#25d366] text-[#0d1117]' : 'border border-[#25d366] bg-[#1a2a3f] text-[#25d366]'}`}>
                        {isBodyValid ? <FaCheckCircle className="text-[11px]" /> : '3'}
                      </span>
                      <h2 className="text-sm font-semibold text-[#e6edf3]">Cuerpo del mensaje <span className="text-[#f85149]">*</span></h2>
                    </div>
                    <span className="text-xs text-[#8b949e]">{bodyCharCount}/1024</span>
                  </div>

                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-[#8b949e]">Insertar variables dinámicas</p>
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <button type="button" onClick={() => insertBodyVariable('nombre')} className="rounded-lg border border-[#25d36640] bg-[#25d36614] px-3 py-1.5 text-xs font-semibold text-[#25d366]">+ {'{{nombre}}'}</button>
                    <button type="button" onClick={() => insertBodyVariable(2)} className="rounded-lg border border-[#25d36640] bg-[#25d36614] px-3 py-1.5 text-xs font-semibold text-[#25d366]">+ {'{{2}}'}</button>
                    <button type="button" onClick={() => insertBodyVariable(3)} className="rounded-lg border border-[#25d36640] bg-[#25d36614] px-3 py-1.5 text-xs font-semibold text-[#25d366]">+ {'{{3}}'}</button>
                    <span className="text-xs text-[#8b949e]">Se personalizarán al enviar</span>
                  </div>

                  <div className="flex items-center gap-1 rounded-t-lg border border-[#30363d] border-b-0 bg-[#21262d] px-2 py-1.5">
                    <button type="button" onClick={() => applyInlineFormat('bold')} className="rounded p-1.5 text-[#8b949e] transition hover:bg-[rgba(255,255,255,0.08)]"><FaBold className="text-[12px]" /></button>
                    <button type="button" onClick={() => applyInlineFormat('italic')} className="rounded p-1.5 text-[#8b949e] transition hover:bg-[rgba(255,255,255,0.08)]"><FaItalic className="text-[12px]" /></button>
                    <button type="button" onClick={() => applyInlineFormat('strike')} className="rounded p-1.5 text-[#8b949e] transition hover:bg-[rgba(255,255,255,0.08)]"><FaUnderline className="text-[12px]" /></button>
                    <span className="ml-2 text-xs text-[#8b949e]">*negrita* _cursiva_ ~tachado~</span>
                  </div>

                  <div className="rounded-b-lg border border-[#30363d] bg-[#21262d]">
                  {!editorFallback ? (
                    <Editor
                      key={editorMountKey}
                      tinymceScriptSrc={tinymceSrc}
                      onInit={(evt, editor) => {
                        editorRef.current = editor;
                        editorReadyRef.current = true;
                        setEditorReady(true);
                        setEditorFallback(false);
                      }}
                      value={formData.body_text}
                      onEditorChange={(newValue) => setFormData({ ...formData, body_text: newValue })}
                      init={{
                      license_key: 'gpl',
                      skin: dark ? 'oxide-dark' : 'oxide',
                      content_css: dark ? 'dark' : 'default',
                      menubar: false,
                      height: 260,
                      plugins: 'lists emoticons link image media code table',
                      toolbar: 'undo redo | bold italic underline | emoticons | bullist numlist | alignleft aligncenter alignright | link | headerimage headervideo | code',
                      branding: false,
                      promotion: false,
                      content_style: dark
                        ? 'body { font-family: Helvetica, Arial, sans-serif; font-size: 14px; background: #0b1423; color: #e6edf3; } p, span, div { color: #e6edf3; } a { color: #58a6ff; }'
                        : 'body { font-family: Helvetica, Arial, sans-serif; font-size: 14px; background: #ffffff; color: #14263f; } p, span, div { color: #14263f; } a { color: #1d5fae; }',
                      paste_as_text: true,
                      file_picker_types: 'image media',
                      file_picker_callback: async (cb, value, meta) => {
                        try {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = meta.filetype === 'image' ? 'image/*' : (meta.filetype === 'media' ? 'video/*' : '*/*');
                          input.onchange = async () => {
                            const file = input.files && input.files[0];
                            if (!file) return;
                            const fd = new FormData();
                            fd.append('file', file);
                            const resp = await api.post('meta-templates/upload-header-media/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                            const url = resp.data?.url || '';
                            const kind = (resp.data?.type || '').toUpperCase();
                            if (url) {
                              if (kind === 'IMAGE') {
                                cb(url, { title: file.name });
                                setFormData(prev => ({ ...prev, header_type: 'IMAGE' }));
                              } else if (kind === 'VIDEO') {
                                cb(url, { title: file.name });
                                setFormData(prev => ({ ...prev, header_type: 'VIDEO' }));
                              } else {
                                // Document: insertar link
                                cb(url, { text: file.name });
                                setFormData(prev => ({ ...prev, header_type: 'DOCUMENT' }));
                              }
                              setHeaderMediaUrl(url);
                            } else {
                              alert('Error al subir archivo.');
                            }
                          };
                          input.click();
                        } catch (e) {
                          alert('Fallo al seleccionar/subir archivo: ' + (e.response?.data?.detail || e.message));
                        }
                      },
                      setup: (editor) => {
                        editor.ui.registry.addButton('headerimage', {
                          tooltip: 'Insertar imagen (Header)',
                          icon: 'image',
                          onAction: () => {
                            try {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = 'image/*';
                              input.onchange = async () => {
                                const file = input.files && input.files[0];
                                if (!file) return;
                                const fd = new FormData();
                                fd.append('file', file);
                                const resp = await api.post('meta-templates/upload-header-media/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                                const url = resp.data?.url || '';
                                if (url) {
                                  editor.insertContent(`<img src="${url}" alt="" />`);
                                  setFormData(prev => ({ ...prev, header_type: 'IMAGE' }));
                                  setHeaderMediaUrl(url);
                                } else {
                                  alert('Error al subir imagen.');
                                }
                              };
                              input.click();
                            } catch (e) {
                              alert('Fallo al subir imagen: ' + (e.response?.data?.detail || e.message));
                            }
                          }
                        });
                        editor.ui.registry.addButton('headervideo', {
                          tooltip: 'Insertar video (Header)',
                          icon: 'embed',
                          onAction: () => {
                            try {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = 'video/*';
                              input.onchange = async () => {
                                const file = input.files && input.files[0];
                                const fd = new FormData();
                                fd.append('file', file);
                                const resp = await api.post('meta-templates/upload-header-media/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                                const url = resp.data?.url || '';
                                if (url) {
                                  editor.insertContent(`<video src="${url}" controls></video>`);
                                  setFormData(prev => ({ ...prev, header_type: 'VIDEO' }));
                                  setHeaderMediaUrl(url);
                                } else {
                                  alert('Error al subir video.');
                                }
                              };
                              input.click();
                            } catch (e) {
                              alert('Fallo al subir video: ' + (e.response?.data?.detail || e.message));
                            }
                          }
                        });
                      }
                    }}
                    />
                  ) : (
                    <div className="p-0">
                      <div className="flex items-center justify-between gap-2 border-b border-[#8b949e66] bg-[#d2992230] p-2 text-xs text-[#f8d47a]">
                        <span>No se pudo cargar el editor enriquecido. Usando modo texto.</span>
                        <button
                          type="button"
                          className="rounded border border-[#d2992266] bg-[#21262d] px-2 py-1 text-[#e6edf3] hover:bg-[#283343]"
                          onClick={() => {
                            editorReadyRef.current = false;
                            setEditorReady(false);
                            setEditorFallback(false);
                            setEditorMountKey((k) => k + 1);
                          }}
                        >
                          Reintentar editor
                        </button>
                      </div>
                      <textarea
                        ref={bodyTextareaRef}
                        value={formData.body_text}
                        onChange={(e) => setFormData({ ...formData, body_text: e.target.value })}
                        className="h-[260px] w-full resize-vertical border-0 bg-[#21262d] p-3 text-[#e6edf3]"
                        placeholder="Escribe el cuerpo del mensaje..."
                      />
                    </div>
                  )}
                  </div>
                <p className="mt-1 text-xs text-[#8b949e]">
                  Usa {'{{1}}'}, {'{{2}}'}, etc. para variables dinamicas. Tambien puedes usar {'{{nombre}}'} en el cuerpo (se normaliza a {'{{1}}'}).
                </p>
                <div className="mt-2 rounded border border-[#d2992266] bg-[#d2992214] p-2">
                  <p className="text-[12px] text-[#e8be62]">
                    Nota: el cuerpo se enviará como texto. Los medios solo se admiten en el <strong>Encabezado</strong> de la plantilla por Meta.
                  </p>
                  {headerMediaUrl && (
                    <p className="mt-1 text-[12px] text-[#25d366]">Medio asignado al Encabezado: <a href={headerMediaUrl} target="_blank" rel="noreferrer" className="underline">{headerMediaUrl}</a>. Tipo: {formData.header_type || '—'}</p>
                  )}
                </div>
                </section>

                <div className="h-px bg-[#30363d]" />

                <section>
                <div className="mb-3 flex items-center gap-2">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#25d366] text-[#0d1117]"><FaCheckCircle className="text-[11px]" /></span>
                  <h2 className="text-sm font-semibold text-[#e6edf3]">Pie (Footer) <span className="ml-1 text-xs font-normal text-[#8b949e]">(opcional)</span></h2>
                </div>
                <input
                  type="text"
                  value={formData.footer_text}
                  onChange={(e) => setFormData({...formData, footer_text: e.target.value})}
                  className="w-full rounded-lg border border-[#30363d] bg-[#21262d] px-3 py-2.5 text-sm text-[#e6edf3] outline-none"
                  placeholder="Texto pequeño al final del mensaje. Ej: WaveSales"
                  maxLength={60}
                />
                </section>

                <div className="h-px bg-[#30363d]" />

                <section>
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-[#e6edf3]">Botones de acción <span className="ml-1 text-xs font-normal text-[#8b949e]">(máx. 3, opcional)</span></h2>
                  <button
                    type="button"
                    onClick={addButton}
                    disabled={formData.buttons.length >= 3}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-[#25d36640] bg-[#25d36614] px-3 py-1.5 text-xs font-semibold text-[#25d366] transition disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <FaPlus className="text-[11px]" />
                    Agregar botón
                  </button>
                </div>

                {formData.buttons.length === 0 ? (
                  <div className="rounded-lg border-2 border-dashed border-[#30363d] p-5 text-center text-xs text-[#8b949e]">
                    Sin botones. Puedes agregar hasta 3 botones de respuesta rápida, URL o llamada.
                  </div>
                ) : (
                  <div className="space-y-3">
                {formData.buttons.map((btn, i) => (
                  <div key={i} className="space-y-2 rounded-lg border border-[#30363d] bg-[rgba(255,255,255,0.02)] p-3">
                    <div className="text-xs font-semibold text-[#8b949e]">Botón {i + 1}</div>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { value: 'QUICK_REPLY', label: 'Respuesta rápida' },
                        { value: 'URL', label: 'Abrir URL' },
                        { value: 'PHONE_NUMBER', label: 'Llamar' },
                      ].map((opt) => (
                        <button
                          key={`${i}-${opt.value}`}
                          type="button"
                          onClick={() => updateButton(i, 'type', opt.value)}
                          className="rounded border py-1.5 text-xs font-semibold transition"
                          style={{ background: btn.type === opt.value ? 'rgba(37,211,102,0.1)' : '#21262d', borderColor: btn.type === opt.value ? 'rgba(37,211,102,0.4)' : '#30363d', color: btn.type === opt.value ? '#25d366' : '#8b949e' }}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>

                    <div className="flex gap-2">
                    <select
                      value={btn.type}
                      onChange={(e) => updateButton(i, 'type', e.target.value)}
                      className="rounded border border-[#30363d] bg-[#21262d] px-3 py-2 text-sm text-[#e6edf3]"
                    >
                      <option value="QUICK_REPLY">Respuesta Rápida</option>
                      <option value="PHONE_NUMBER">Llamar</option>
                      <option value="URL">URL</option>
                    </select>
                    <input
                      type="text"
                      value={btn.text}
                      onChange={(e) => updateButton(i, 'text', e.target.value)}
                      className="flex-1 rounded border border-[#30363d] bg-[#21262d] px-3 py-2 text-sm text-[#e6edf3]"
                      placeholder="Texto del botón"
                      maxLength={25}
                    />
                    {(btn.type === 'PHONE_NUMBER' || btn.type === 'URL') && (
                      <input
                        type="text"
                        value={btn.value || ''}
                        onChange={(e) => updateButton(i, 'value', e.target.value)}
                        className="flex-1 rounded border border-[#30363d] bg-[#21262d] px-3 py-2 text-sm text-[#e6edf3]"
                        placeholder={btn.type === 'PHONE_NUMBER' ? '+123456789' : 'https://...'}
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => removeButton(i)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded border border-[#f8514966] bg-[#f851491a] text-[#f85149] transition hover:bg-[#f8514930]"
                    >
                      <FaTrash className="text-[11px]" />
                    </button>
                    </div>
                  </div>
                ))}
                  </div>
                )}
                </section>
              </form>

              <aside className="plantillas-form-preview max-h-[calc(100vh-260px)] space-y-5 overflow-y-auto p-5 xl:p-6" style={{ background: 'rgba(255,255,255,0.01)' }}>
                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <FaMobileAlt className="text-[13px] text-[#25d366]" />
                    <h3 className="text-xs font-semibold uppercase tracking-[0.08em] text-[#8b949e]">Vista previa en vivo</h3>
                  </div>

                  <div className="mx-auto w-[280px] overflow-hidden rounded-2xl border-4 border-[#21262d]">
                    <div className="flex items-center justify-between bg-black px-3 py-1.5">
                      <span className="text-xs text-white">9:41</span>
                      <span className="h-1.5 w-3 rounded-sm bg-white" />
                    </div>

                    <div className="flex items-center gap-2 bg-[#075e54] px-3 py-2">
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#25d366] text-xs font-bold text-[#0d1117]">W</div>
                      <div>
                        <p className="text-xs font-medium leading-tight text-white">WaveSales</p>
                        <p className="text-[9px] leading-tight text-white/65">Negocio verificado ✓</p>
                      </div>
                    </div>

                    <div className="min-h-[170px] bg-[#0a1a15] p-3">
                      {(formData.body_text || formData.header_text || formData.footer_text || formData.buttons.length > 0) ? (
                        <div className="ml-auto max-w-[92%] overflow-hidden rounded-xl bg-[#005c4b]">
                          {formData.header_type === 'TEXT' && formData.header_text && (
                            <div className="px-3 pb-1 pt-3 text-xs font-bold text-white">{formData.header_text}</div>
                          )}
                          {formData.header_type === 'IMAGE' && (
                            <div className="flex h-20 items-center justify-center bg-black/30"><FaImage className="text-xl text-white/40" /></div>
                          )}
                          {formData.header_type === 'VIDEO' && (
                            <div className="flex h-20 items-center justify-center bg-black/30"><FaVideo className="text-xl text-white/40" /></div>
                          )}
                          {formData.header_type === 'DOCUMENT' && (
                            <div className="px-3 py-2 text-[11px] text-white/70">Documento adjunto</div>
                          )}

                          {formData.body_text && (
                            <div className="px-3 pt-2 text-[11px] leading-relaxed text-white [&_p]:m-0 [&_p+p]:mt-1" dangerouslySetInnerHTML={{ __html: formData.body_text }} />
                          )}

                          {formData.footer_text && (
                            <div className="px-3 pb-1 pt-1 text-[9px] text-white/55">{formData.footer_text}</div>
                          )}

                          <div className="flex justify-end px-3 pb-2 text-[9px] text-white/45">12:00 ✓✓</div>

                          {formData.buttons.filter((b) => (b.text || '').trim()).length > 0 && (
                            <div className="border-t border-white/15">
                              {formData.buttons.filter((b) => (b.text || '').trim()).map((btn, idx) => (
                                <div key={`preview-btn-${idx}`} className="border-b border-white/10 px-3 py-2 text-center text-[11px] font-medium text-[#4fc3f7] last:border-0">
                                  {btn.type === 'URL' ? '🔗 ' : btn.type === 'PHONE_NUMBER' ? '📞 ' : ''}{btn.text}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex h-24 items-center justify-center text-[10px] text-white/20">El mensaje aparecerá aquí</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-[#30363d] bg-[rgba(255,255,255,0.02)] p-4">
                  <p className="mb-3 text-xs font-semibold text-[#e6edf3]">Lista de verificación</p>
                  {[
                    { label: 'Nombre válido', done: isNameValid },
                    { label: 'Categoría seleccionada', done: !!formData.category },
                    { label: 'Idioma seleccionado', done: !!formData.language },
                    { label: 'Cuerpo con contenido', done: isBodyValid },
                  ].map((item) => (
                    <div key={item.label} className="mb-2 flex items-center gap-2 last:mb-0">
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full" style={{ background: item.done ? 'rgba(37,211,102,0.16)' : '#21262d' }}>
                        {item.done ? <FaCheckCircle className="text-[10px] text-[#25d366]" /> : <span className="h-1.5 w-1.5 rounded-full bg-[#8b949e]" />}
                      </span>
                      <span className={`text-sm ${item.done ? 'text-[#e6edf3]' : 'text-[#8b949e]'}`}>{item.label}</span>
                    </div>
                  ))}
                </div>

                <div className="rounded-xl border p-4" style={{ background: `${selectedCategory.color}10`, borderColor: `${selectedCategory.color}33` }}>
                  <p className="mb-1 text-xs font-semibold" style={{ color: selectedCategory.color }}>
                    Categoría: {selectedCategory.label}
                  </p>
                  <p className="text-xs text-[#8b949e]">
                    {selectedCategory.desc}. Revisa que el contenido corresponda a esta categoría para evitar rechazos.
                  </p>
                </div>
              </aside>
            </div>
          </section>
        )}
        
        {!showForm && (
        <div className="plantillas-table-shell w-full overflow-hidden rounded-xl border border-[#30363d] bg-[#161b22] shadow-sm">
          {groupedTemplates.length === 0 ? (
            <div className="p-10 text-center text-[#9cb0c8]">
              <p className="text-lg font-semibold text-gray-100">No hay plantillas creadas aún</p>
              <p className="mt-2 text-sm">Haz clic en + Nueva Plantilla para crear una</p>
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              <table className="w-full min-w-[1200px] table-fixed">
                <colgroup>
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '6%' }} />
                  <col style={{ width: '6%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '11%' }} />
                </colgroup>
                <thead className="bg-[rgba(255,255,255,0.03)]">
                  <tr className="border-b border-[#30363d]">
                    <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-[#8b949e]">
                      <button
                        type="button"
                        onClick={() => setSortMode((prev) => (prev === 'recent' ? 'nameAsc' : prev === 'nameAsc' ? 'nameDesc' : 'recent'))}
                        className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#8b949e] transition hover:bg-[rgba(88,166,255,0.10)] hover:text-[#7fa7d2]"
                        title="Orden: Reciente -> A-Z -> Z-A"
                      >
                        Nombre
                        <span className="text-[10px] normal-case opacity-80">
                          {sortMode === 'recent' ? 'Reciente' : sortMode === 'nameAsc' ? 'A-Z' : 'Z-A'}
                        </span>
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-[#8b949e]">Cat.</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-[#8b949e]">Idioma</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-[#8b949e]">Estado</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-[#8b949e]">Números</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-[#8b949e]">ID Meta</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-[#8b949e]">Razón Rechazo</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-[#8b949e]">Cuerpo</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-[#8b949e]">Acciones</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-[#24344c]">
                  {groupedTemplates.map((g) => (
                    <tr key={g.name} className="transition-colors hover:bg-[rgba(255,255,255,0.02)]">
                      <td className="px-4 py-3 text-[13px] font-semibold text-gray-100 break-words align-top">
                        <span>{g.display_name}</span>
                      </td>

                      <td className="px-4 py-3 text-[11px] text-[#8b949e] break-words align-top">
                        <span className="inline-flex whitespace-nowrap rounded bg-[#21262d] px-1.5 py-0.5" style={{ fontFamily: 'JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>{g.category_display || g.category || '—'}</span>
                      </td>

                      <td className="px-4 py-3 text-[12px] text-[#8b949e] align-top">{g.language || '—'}</td>
                      <td className="px-4 py-3 text-xs align-top">{getStatusBadge(g.status)}</td>

                      <td className="px-4 py-3 text-xs align-top">
                        <div className="flex flex-wrap gap-1.5">
                          {g.phones.map((p) => {
                            const isActive = String(p.phone_number_id || '') === String(activePhoneId || '');
                            const mismatch = !!(String(p.phone_number_id || '') && String(activePhoneId || '') && String(p.phone_number_id) !== String(activePhoneId));
                            return (
                              <span
                                key={`${g.name}-${p.phone_number_id}`}
                                className={`rounded-md border px-2 py-0.5 text-[10px] whitespace-nowrap ${isActive ? 'border-indigo-400/40 bg-indigo-500/15 text-indigo-300' : (mismatch ? 'border-amber-400/40 bg-amber-500/15 text-amber-300' : 'border-[#2a3b54] bg-[#182437] text-[#9cb0c8]')}`}
                                style={{ fontFamily: 'JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}
                                title={`${p.display_phone_number || ''} (${p.phone_number_id || ''})${mismatch ? ' — Plantilla en PID distinto al activo' : ''}`}
                              >
                                {(p.display_phone_number || '').replace('+593', '+593 ')}
                              </span>
                            );
                          })}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-[11px] text-[#8b949e] break-words align-top whitespace-nowrap" title={g.meta_template_id || ''} style={{ fontFamily: 'JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>
                        {g.meta_template_id || '—'}
                      </td>

                      <td className="px-4 py-3 text-[11px] text-[#8b949e] align-top max-w-0 overflow-hidden" title={g.rejection_reason || ''}>
                        <span className={`block w-full truncate ${g.rejection_reason ? 'text-[#f85149]' : 'text-[#7489a5]'}`}>
                          {g.rejection_reason || '—'}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-[11px] text-[#e6edf3] align-top max-w-0 overflow-hidden" title={g.body_text}>
                        <span className="block w-full truncate">{g.body_text || '—'}</span>
                      </td>

                      <td className="px-4 py-3 text-xs align-top">
                        <div className="flex flex-nowrap items-center justify-end gap-1.5">
                          <button
                            onClick={() => openPreview(g)}
                            className="inline-flex h-7 w-[106px] items-center justify-center gap-1.5 rounded-md border border-[#2f5a88] bg-[#1a3f67] px-2 py-1 text-[11px] font-semibold text-[#8ec8ff] transition hover:bg-[#214c7c]"
                            title="Ver vista previa"
                          >
                            <FaEye className="text-[10px]" />
                            Vista previa
                          </button>

                          <div className="w-7">
                            {g.primary?.id ? (
                              <button
                                onClick={() => handleDelete({ id: g.primary.id, name: g.name })}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-300 transition hover:bg-rose-500/15"
                                title="Eliminar"
                              >
                                <FaTrash className="text-[11px]" />
                              </button>
                            ) : (
                              <span className="block h-7 w-7" aria-hidden="true" />
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}

        {previewOpen && previewTemplate && (
          <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/70 p-4">
            <div className="w-full max-w-[360px] rounded-2xl border border-[#2a3b54] bg-[#141d2b] p-4 shadow-2xl">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="truncate text-[33px] font-semibold leading-tight text-[#e6edf3]">
                  Vista previa: {previewTemplate.name}
                </h3>
                <button
                  onClick={() => setPreviewOpen(false)}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-[#3a4c66] bg-[#1b2738] text-[#9cb0c8] transition hover:text-white"
                  aria-label="Cerrar vista previa"
                >
                  ×
                </button>
              </div>

              <div className="mb-3 flex items-center gap-2">
                {previewTemplate?.submitTemplate?.id && canSubmitToMetaStatus(String(previewTemplate?.status || '').trim().toUpperCase()) && (
                  <button
                    onClick={async () => {
                      const sent = await handleSubmitToMeta(previewTemplate.submitTemplate);
                      if (sent) {
                        setPreviewOpen(false);
                        setPreviewTemplate(null);
                      }
                    }}
                    className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-300 transition hover:bg-emerald-500/15"
                  >
                    Enviar a Meta
                  </button>
                )}

                {previewTemplate?.editorTemplate?.id && canSubmitToMetaStatus(String(previewTemplate?.status || '').trim().toUpperCase()) && (
                  <button
                    onClick={() => {
                      const tpl = previewTemplate.editorTemplate;
                      setPreviewOpen(false);
                      setPreviewTemplate(null);
                      handleEdit(tpl);
                    }}
                    className="rounded-md border border-[#58a6ff66] bg-[#58a6ff1f] px-2.5 py-1 text-[11px] font-semibold text-[#8ec8ff] transition hover:bg-[#58a6ff2a]"
                  >
                    Editar
                  </button>
                )}
              </div>

              <div className="rounded-2xl bg-[#0f8b78] p-3">
                {previewTemplate.mediaUrl && previewTemplate.mediaType === 'IMAGE' && (
                  <img src={previewTemplate.mediaUrl} alt="header" className="mb-2 w-full rounded-lg object-cover" />
                )}
                {previewTemplate.mediaUrl && previewTemplate.mediaType === 'VIDEO' && (
                  <video src={previewTemplate.mediaUrl} controls className="mb-2 w-full rounded-lg" />
                )}
                {previewTemplate.mediaUrl && previewTemplate.mediaType === 'DOCUMENT' && (
                  <div className="mb-2 rounded-lg bg-black/20 px-3 py-2 text-xs text-white/85">Documento adjunto</div>
                )}

                <div className="rounded-xl bg-[#c4e2b0] px-3 py-2.5 text-[#22301a]">
                  <div className="whitespace-pre-wrap break-words text-[12px] font-medium leading-5">{previewTemplate.text}</div>
                  <div className="mt-2 text-right text-[11px] text-[#6b7f62]">
                    {new Date().toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })} ✓✓
                  </div>
                </div>
              </div>

              <div className="mt-3 flex items-center gap-2 text-[12px] text-[#9cb0c8]">
                {getStatusBadge(previewTemplate?.status)}
                <span className="truncate">ID: {previewTemplate?.metaTemplateId || '—'}</span>
              </div>
            </div>
          </div>
        )}
        </div>
      </main>
    </div>
  );
}

export default PlantillasCoexistencia;
